import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  IconButton,
  Collapse,
  Chip,
  Button,
  Divider,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Folder as FolderIcon,
  Description as DescriptionIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import { getProjectCostSheetCategories } from '../../services/api';
import { useTranslation } from 'react-i18next';

const CostSheetSelector = ({ projectId, selectedCostSheets = [], onCostSheetsChange }) => {
  const { t } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (projectId) {
      loadCostSheetCategories();
    }
  }, [projectId]);

  const loadCostSheetCategories = async () => {
    try {
      setLoading(true);
      const response = await getProjectCostSheetCategories(projectId);
      setCategories(response.data);
    } catch (error) {
      console.error('Error loading cost sheet categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (categoryId) => {
    setExpanded(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const handleCostSheetSelect = (costSheet) => {
    const isSelected = selectedCostSheets.some(cs => cs.id === costSheet.id);
    
    if (isSelected) {
      // Remove from selection
      const updated = selectedCostSheets.filter(cs => cs.id !== costSheet.id);
      onCostSheetsChange(updated);
    } else {
      // Add to selection
      const updated = [...selectedCostSheets, costSheet];
      onCostSheetsChange(updated);
    }
  };

  const handleRemoveCostSheet = (costSheetId) => {
    const updated = selectedCostSheets.filter(cs => cs.id !== costSheetId);
    onCostSheetsChange(updated);
  };

  const isCostSheetSelected = (costSheetId) => {
    return selectedCostSheets.some(cs => cs.id === costSheetId);
  };

  const renderCategory = (category, level = 0) => {
    const isExpanded = expanded[category.id];
    const hasChildren = category.children && category.children.length > 0;
    const hasCostSheets = category.costSheets && category.costSheets.length > 0;
    const isExpandable = hasChildren || hasCostSheets;
    const indentLevel = level * 24;

    return (
      <Box key={category.id} sx={{ ml: `${indentLevel}px` }}>
        {/* Category Header */}
        <ListItem
          disablePadding
          sx={{
            minHeight: 40,
            '&:hover': {
              backgroundColor: 'action.hover',
            }
          }}
        >
          <ListItemButton
            onClick={() => isExpandable && handleToggle(category.id)}
            sx={{ pl: 1 }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              {isExpandable ? (
                isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />
              ) : (
                <Box sx={{ width: 24 }} />
              )}
            </ListItemIcon>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <FolderIcon color="primary" />
            </ListItemIcon>
            <ListItemText
              primary={category.name}
              secondary={category.code && `Code: ${category.code}`}
            />
          </ListItemButton>
        </ListItem>

        {/* Category Cost Sheets */}
        {hasCostSheets && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {category.costSheets.map((costSheet) => (
                <ListItem
                  key={costSheet.id}
                  disablePadding
                  sx={{
                    ml: `${indentLevel + 24}px`,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    }
                  }}
                >
                  <ListItemButton
                    onClick={() => handleCostSheetSelect(costSheet)}
                    sx={{ pl: 1 }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <DescriptionIcon color="secondary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={costSheet.name}
                      secondary={
                        <Box>
                          <Typography variant="caption" display="block">
                            {costSheet.code && `Code: ${costSheet.code}`}
                          </Typography>
                          <Typography variant="caption" display="block">
                            {costSheet.workType && `Type: ${costSheet.workType}`}
                          </Typography>
                          <Typography variant="caption" display="block">
                            {costSheet.quantity} {costSheet.unit} × {costSheet.unitPrice?.toFixed(2)}€ = {costSheet.totalPrice?.toFixed(2)}€
                          </Typography>
                        </Box>
                      }
                    />
                    {isCostSheetSelected(costSheet.id) && (
                      <CheckIcon color="primary" />
                    )}
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Collapse>
        )}

        {/* Child Categories */}
        {hasChildren && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {category.children.map((child) => renderCategory(child, level + 1))}
            </List>
          </Collapse>
        )}
      </Box>
    );
  };

  return (
    <Grid container spacing={2}>
      {/* Available Cost Sheets */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2, height: 400, overflow: 'auto' }}>
          <Typography variant="h6" gutterBottom>
            {t('costSheetsPage.title')}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          {loading ? (
            <Typography>{t('common.loading', { defaultValue: 'Loading cost sheets...' })}</Typography>
          ) : categories.length > 0 ? (
            <List dense>
              {categories.map((category) => renderCategory(category))}
            </List>
          ) : (
            <Typography color="text.secondary">
              {t('costSheetsPage.uncategorized', { defaultValue: 'No cost sheets found for this project.' })}
            </Typography>
          )}
        </Paper>
      </Grid>

      {/* Selected Cost Sheets */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2, height: 400, overflow: 'auto' }}>
          <Typography variant="h6" gutterBottom>
            {t('common.selected', { defaultValue: 'Selected' })} {t('costSheetsPage.title')} ({selectedCostSheets.length})
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          {selectedCostSheets.length > 0 ? (
            <List dense>
              {selectedCostSheets.map((costSheet) => (
                <Card key={costSheet.id} sx={{ mb: 1 }}>
                  <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          {costSheet.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {costSheet.code && `Code: ${costSheet.code}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {costSheet.workType && `Type: ${costSheet.workType}`}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                          <Chip 
                            label={`${costSheet.quantity} ${costSheet.unit}`} 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                          />
                          <Chip 
                            label={`${costSheet.unitPrice?.toFixed(2)}€`} 
                            size="small" 
                            color="secondary" 
                            variant="outlined"
                          />
                          <Chip 
                            label={`${costSheet.totalPrice?.toFixed(2)}€`} 
                            size="small" 
                            color="success" 
                            variant="outlined"
                          />
                        </Box>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveCostSheet(costSheet.id)}
                        color="error"
                      >
                        <RemoveIcon />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </List>
          ) : (
            <Typography color="text.secondary">
              {t('common.noData', { defaultValue: 'No cost sheets selected. Click on cost sheets from the left panel to add them.' })}
            </Typography>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

export default CostSheetSelector;
